import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { isSearchSpace, SpaceInfo } from '../../../app/state/SpaceInfo';
import { Telemetry } from '../../../commonInfra/services/Telemetry';
import { SupportedContentTypes } from '../../../home/types/HomeTypes';
import { searchTelemetry } from '../../../search/components/SearchTelemetry';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { ContentTelemetryEvents, TelemetryEvents } from '../TelemetryEvents';
import {
  ActionMenuItemClickedEvent,
  ContentClickedEvent,
  ContentLocation,
  ContentTelemetryCommonData,
  ContentTelemetryContextSpecificData,
  FavoriteToggleClickedEvent,
  FavoriteToggleType,
} from '../types/ContentTelemetryTypes';

export function generateTelemetryEventHandler(
  type: ContentTelemetryEvents,
  spaceInfo: SpaceInfo,
  allContents: ReadonlyArray<SupportedContentTypes>,
  contentInfo: IContentInfo,
  contentLocation: ContentLocation,
  contentPosition: number,
  specificData: ContentTelemetryContextSpecificData): (...args: any[]) => void {
  let collectionContentLocation;
  if (contentLocation !== ContentLocation.SpaceHeader) {
    if(spaceInfo.spaceType === 'collections'){
      collectionContentLocation = ContentLocation.Collections;
    } else if(spaceInfo.spaceType === 'collection') {
      collectionContentLocation = ContentLocation.Collection;
    }
  }
  const commonData: ContentTelemetryCommonData = {
    contentId: contentInfo.id,
    contentType: contentInfo.contentType,
    contentLocation: collectionContentLocation || contentLocation,
    space: spaceInfo.spaceType,
    subspace: spaceInfo.subspaceName,
    contentPosition: contentPosition,
  };

  // if the content is a recommendation, add meta data to telemetry event
  if (commonData.contentType && commonData.contentType === 'recommendedView') {
    specificData = {
      ...specificData,
      recommendationsMetadata: JSON.stringify({
        modelId: contentInfo.modelId,
        seenViews: allContents.slice(0, contentPosition).map(x => parseInt(x.id, 10))
      })
    };
  }

  switch (type) {
    case TelemetryEvents.ContentClicked: {
      return function onContentClick() {
        if (isSearchSpace(spaceInfo)) {
          searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: 'search-space',
            resultType: spaceInfo.subspaceName,
            seeAll: false,
            index: contentPosition,
            total: (specificData as any).contentTotal,
            searchTerm: (specificData as any).searchTerm,
            contentId: contentInfo.id,
          });
        } else {
          Telemetry.sendEvent<ContentClickedEvent>(
            { ...commonData, ...specificData, type: TelemetryEvents.ContentClicked }
          );
        }
      };
    }
    case TelemetryEvents.FavoriteToggleClicked: {
      return function onFavoriteToggleClick(toggleType: FavoriteToggleType) {
        Telemetry.sendEvent<FavoriteToggleClickedEvent>(
          { ...commonData, ...specificData, type: TelemetryEvents.FavoriteToggleClicked, toggleType }
        );
      };
    }
    case TelemetryEvents.ActionMenuItemClicked: {
      return function onActionMenuItemClick(actionType: string) {
        // For action menu item click, also record the user's site role
        const sessionManagementService = getSessionManagementServiceInstance();
        // Explicitly check for Server Admin because sessionSiteRole method does not return Server Admin
        const userSiteRole = sessionManagementService.isSessionServerAdmin()
          ? SiteRoles.ServerAdministrator
          : sessionManagementService.sessionSiteRole();

        Telemetry.sendEvent<ActionMenuItemClickedEvent>({
          ...commonData,
          ...specificData,
          type: TelemetryEvents.ActionMenuItemClicked,
          actionType,
          userSiteRole
        });
      };
    }
  }
}
